import { Title } from '@solidjs/meta'
import { A, useParams } from '@solidjs/router'
import { type Component, createEffect, createSignal, onCleanup, Show } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import { checkoutService, type PublicOrderStatusType } from '~/services/checkout.service'

const money = (value: number) => `$${value.toLocaleString('es-CO')}`

/**
 * A dónde vuelve el comprador desde la pasarela.
 *
 * El aviso de la pasarela y esta redirección son independientes y llegan en
 * cualquier orden: es normal aterrizar aquí antes de que el pago esté
 * confirmado. Por eso la página no da nada por hecho y **escucha en vivo** (SSE)
 * hasta que el pedido se resuelve.
 *
 * Nada de lo que trae la URL (`payment_id`, `status`) se cree: lo escribe el
 * navegador. El estado sale siempre del backend, que a su vez lo confirma con la
 * pasarela.
 */
const PedidoPublicoPage: Component = () => {
  const params = useParams()
  const empresa = () => params.empresa ?? ''
  const numero = () => params.numero ?? ''

  const [status, setStatus] = createSignal<PublicOrderStatusType | null>(null)
  const [total, setTotal] = createSignal<number | null>(null)
  const [noEncontrado, setNoEncontrado] = createSignal(false)

  createEffect(() => {
    const e = empresa()
    const n = numero()
    if (!e || !n) return

    let cerrado = false

    // Primero el estado actual, por si ya está resuelto: quien llega tarde no
    // debe quedarse mirando un "procesando" que nunca cambiará.
    checkoutService
      .status(e, n)
      .then(order => {
        if (cerrado) return
        setStatus(order.status)
        setTotal(order.total)
      })
      .catch(() => {
        if (!cerrado) setNoEncontrado(true)
      })

    const detener = checkoutService.streamStatus(e, n, event => {
      if (cerrado) return
      setStatus(event.status)
      if (typeof event.total === 'number') setTotal(event.total)
    })

    onCleanup(() => {
      cerrado = true
      detener()
    })
  })

  const pendiente = () => status() === null || status() === 'pending'

  return (
    <>
      <Title>Tu pedido</Title>

      <Container class="py-12">
        <div class="mx-auto max-w-lg">
          <Show
            when={!noEncontrado()}
            fallback={
              <Card>
                <Card.Body>
                  <div class="flex flex-col items-center gap-4 py-10 text-center">
                    <Icon icon="material-symbols-light:search-off" size={52} class="text-foreground-subtle" />
                    <div>
                      <p class="text-lg font-medium text-foreground">No encontramos ese pedido</p>
                      <p class="text-sm text-foreground-subtle">Comprueba el enlace o escríbenos si acabas de pagar.</p>
                    </div>
                    <A href={`/catalogo/${empresa()}`}>
                      <Button>Volver al catálogo</Button>
                    </A>
                  </div>
                </Card.Body>
              </Card>
            }
          >
            <Card>
              <Card.Body>
                <div class="flex flex-col items-center gap-4 py-8 text-center">
                  {/* Pendiente: el pago se está confirmando ahora mismo. */}
                  <Show when={pendiente()}>
                    <Icon icon="material-symbols-light:refresh" size={40} class="animate-spin text-primary" />
                    <div>
                      <p class="text-lg font-medium text-foreground">Confirmando tu pago…</p>
                      <p class="text-sm text-foreground-subtle">Puede tardar unos segundos. No cierres esta página.</p>
                    </div>
                  </Show>

                  <Show when={status() === 'paid'}>
                    <div class="flex h-16 w-16 items-center justify-center rounded-full bg-success-soft">
                      <Icon icon="material-symbols-light:check-circle-outline" size={40} class="text-success" />
                    </div>
                    <div>
                      <p class="text-xl font-bold text-foreground">¡Pago confirmado!</p>
                      <p class="text-sm text-foreground-subtle">Te enviamos el comprobante por correo.</p>
                    </div>
                  </Show>

                  <Show when={status() === 'cancelled' || status() === 'expired'}>
                    <div class="flex h-16 w-16 items-center justify-center rounded-full bg-danger-soft">
                      <Icon icon="material-symbols-light:cancel-outline" size={40} class="text-danger" />
                    </div>
                    <div>
                      <p class="text-xl font-bold text-foreground">El pedido no se completó</p>
                      <p class="text-sm text-foreground-subtle">No se realizó ningún cobro. Puedes intentarlo de nuevo cuando quieras.</p>
                    </div>
                  </Show>

                  <Show when={status() === 'refunded'}>
                    <div class="flex h-16 w-16 items-center justify-center rounded-full bg-info-soft">
                      <Icon icon="material-symbols-light:undo" size={40} class="text-info" />
                    </div>
                    <div>
                      <p class="text-xl font-bold text-foreground">Pedido reembolsado</p>
                      <p class="text-sm text-foreground-subtle">El dinero vuelve a tu medio de pago según los plazos de tu banco.</p>
                    </div>
                  </Show>

                  <dl class="w-full space-y-1 rounded-lg border border-border p-4 text-sm">
                    <div class="flex justify-between">
                      <dt class="text-foreground-subtle">Pedido</dt>
                      <dd class="font-semibold text-foreground">{numero()}</dd>
                    </div>
                    <Show when={total() != null}>
                      <div class="flex justify-between">
                        <dt class="text-foreground-subtle">Total</dt>
                        <dd class="font-semibold text-foreground">{money(total() as number)}</dd>
                      </div>
                    </Show>
                  </dl>

                  <p class="text-xs text-foreground-subtle">Guarda este número: es con el que podemos localizar tu compra.</p>

                  <A href={`/catalogo/${empresa()}`} class="w-full">
                    <Button fullWidth variant="outlined" color="default">
                      Seguir comprando
                    </Button>
                  </A>
                </div>
              </Card.Body>
            </Card>
          </Show>
        </div>
      </Container>
    </>
  )
}

export default PedidoPublicoPage
